import {
  ForwardedRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { SearchInputProps } from "./types";
import { useIsoMorphicEffect } from "@shared/hooks/useIsoMorphicEffect";

export const useSearchInput = ({
  searchValue = "",
  focusTrigger = false,
  onEnter,
  onChangeInput,
  onClickCancel,
  onFocus,
  ref,
}: Pick<
  SearchInputProps,
  | "searchValue"
  | "focusTrigger"
  | "onEnter"
  | "onChangeInput"
  | "onClickCancel"
  | "onFocus"
> & { ref: ForwardedRef<HTMLInputElement> }) => {
  const [value, setValue] = useState(searchValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const localRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => localRef.current as HTMLInputElement);

  useEffect(() => {
    setValue(searchValue);
  }, [searchValue]);

  const focusInput = () => {
    const el = localRef.current;
    if (!el) return;

    el.focus({ preventScroll: true });

    const len = el.value.length;
    try {
      el.setSelectionRange(len, len);
    } catch {}
  };

  useIsoMorphicEffect(() => {
    if (!focusTrigger) return;

    focusInput();
  }, [focusTrigger]);

  useEffect(() => {
    setValue(searchValue);
  }, [searchValue]);

  const handleChange = (val: string) => {
    setValue(val);
    onChangeInput?.(val);
  };

  const handleEnter = (val: string) => {
    if (val !== "") {
      onEnter?.(val);
    }
  };

  const handleCancel = () => {
    setValue("");
    onClickCancel?.();
  };

  const handleFocus = () => {
    onChangeInput?.(value);
    onFocus?.();
  };

  return {
    value,
    inputRef,
    handleChange,
    handleEnter,
    handleCancel,
    handleFocus,
  };
};
